import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import {
  ARTICLE_TYPE_LABEL,
  createArticle,
  getArticle,
  listCategories,
  updateArticle,
  type ArticleType,
  type CategoryNode,
} from "../api/knowledge";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ALL_ROLES, ROLE_LABELS, type AcademyRole } from "../types/user";

function canEdit(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

function translit(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, "yo")
    .replace(/[а-я]/g, (c) => {
      const m: Record<string, string> = {
        а: "a", б: "b", в: "v", г: "g", д: "d", е: "e",
        ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
        н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u",
        ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch",
        ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
      };
      return m[c] ?? c;
    })
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function flatten(nodes: CategoryNode[], depth = 0): { id: number; label: string }[] {
  const out: { id: number; label: string }[] = [];
  for (const n of nodes) {
    out.push({ id: n.id, label: "— ".repeat(depth) + n.name });
    if (n.children.length) out.push(...flatten(n.children, depth + 1));
  }
  return out;
}

export function ArticleEditPage() {
  const params = useParams<{ articleId: string }>();
  const isNew = params.articleId === "new";
  const id = isNew ? null : Number(params.articleId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [content, setContent] = useState<string | undefined>("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [articleType, setArticleType] = useState<ArticleType>("article");
  const [allowedRoles, setAllowedRoles] = useState<Set<AcademyRole>>(new Set());
  const [categories, setCategories] = useState<{ id: number; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cats = await listCategories();
      setCategories(flatten(cats.items));
      if (!isNew && id != null && Number.isFinite(id)) {
        const a = await getArticle(id);
        setTitle(a.title);
        setSlug(a.slug);
        setSlugTouched(true);
        setContent(a.content_markdown);
        setCategoryId(a.category_id ?? "");
        setArticleType(a.article_type);
        setAllowedRoles(new Set(a.allowed_roles));
      }
      setDirty(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Статья не найдена");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;
  if (!canEdit(user.roles)) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <p className="text-slate-500">Нет прав на редактирование.</p>
      </div>
    );
  }

  function mark() {
    setDirty(true);
  }

  function toggleRole(r: AcademyRole) {
    setAllowedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
    mark();
  }

  function handleTitleChange(v: string) {
    setTitle(v);
    mark();
    if (!slugTouched) setSlug(translit(v));
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (isNew) {
        const a = await createArticle({
          title: title.trim(),
          slug: slug.trim(),
          content_markdown: content ?? "",
          article_type: articleType,
          category_id: categoryId === "" ? undefined : categoryId,
          allowed_roles: Array.from(allowedRoles),
        });
        navigate(`/kb/articles/${a.id}`);
      } else if (id) {
        await updateArticle(id, {
          title,
          content_markdown: content ?? "",
          article_type: articleType,
          category_id: categoryId === "" ? null : categoryId,
          allowed_roles: Array.from(allowedRoles),
        });
        setDirty(false);
        navigate(`/kb/articles/${id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "SLUG_TAKEN") {
        alert("Статья с таким slug уже существует");
      } else {
        alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <p className="text-slate-500">{error}</p>
        <Link to="/kb" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← в базу знаний
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-color-mode="light">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-2">
        <div>
          <Link to="/kb" className="text-sm text-slate-500 hover:text-brand-accent">
            ← база знаний
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">
            {isNew ? "Новая статья" : "Редактирование"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${dirty ? "text-amber-600" : "text-green-600"}`}>
            {dirty ? "не сохранено" : "✓ сохранено"}
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? "Сохраняем…" : isNew ? "Создать" : "Сохранить"}
          </button>
        </div>
      </header>

      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Название</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Slug</label>
            <input
              type="text"
              required
              pattern="[a-z0-9\-]+"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
                mark();
              }}
              className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
              disabled={!isNew}
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Тип</label>
          <div className="flex flex-wrap gap-1.5">
            {(["article", "regulation", "script", "checklist"] as ArticleType[]).map(
              (t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setArticleType(t);
                    mark();
                  }}
                  className={`rounded-full px-3 py-1.5 text-sm transition ${
                    articleType === t
                      ? "bg-brand text-white"
                      : "bg-surface-alt text-ink-soft hover:bg-line"
                  }`}
                >
                  {ARTICLE_TYPE_LABEL[t]}
                </button>
              ),
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            Влияет только на отображение бейджа и фильтр; контент один и тот же.
          </p>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Категория</label>
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value ? Number(e.target.value) : "");
              mark();
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">— без категории —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Доступно ролям (пусто = всем авторизованным)
          </label>
          <div className="flex flex-wrap gap-2">
            {ALL_ROLES.map((r) => (
              <label
                key={r}
                className="flex cursor-pointer items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs"
              >
                <input
                  type="checkbox"
                  checked={allowedRoles.has(r)}
                  onChange={() => toggleRole(r)}
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Содержимое</label>
          <div className="rounded-lg bg-white shadow-sm">
            <MDEditor
              value={content}
              onChange={(v) => {
                setContent(v ?? "");
                mark();
              }}
              height={500}
              preview="live"
              visibleDragbar={false}
              textareaProps={{
                placeholder:
                  "Markdown. Доступны admonitions: :::important / :::tip / :::example / :::mistake / :::script",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
